import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
  Toast,
  ToastToggle,
} from "flowbite-react";
import { HiHome, HiClipboard, HiCheck, HiX, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import {
  getExternalOnboardingPaymentLinks,
  getAllExternalMemberships,
  deletePaymentLink,
  createPaymentLink,
} from "../../services/payments";
import type {
  PaymentLink,
  ExternalMembership,
  CreatePaymentLinkRequest,
} from "../../types/payments";
import { blueThemeSpinner } from "../exercises/ExerciseModal";
import DeleteButton from "../../components/DeleteButton";
import ConfirmationModal from "../../components/ConfirmationModal";

const ManualOnboardingPage: FC = function () {
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([]);
  const [externalMemberships, setExternalMemberships] = useState<
    ExternalMembership[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [loadingMemberships, setLoadingMemberships] = useState(true);
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<"success" | "error" | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<PaymentLink | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] =
    useState<CreatePaymentLinkRequest>({
      title: "",
      interval: "month",
      price: 0,
    });
  const [priceInput, setPriceInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const showToast = (message: string, type: "success" | "error") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastType(null);
      setToastMessage("");
    }, 5000);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadingMemberships(true);

        const [paymentLinksResponse, membershipsResponse] = await Promise.all([
          getExternalOnboardingPaymentLinks(),
          getAllExternalMemberships(),
        ]);

        setPaymentLinks(paymentLinksResponse.results);
        setExternalMemberships(membershipsResponse.results);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        showToast("Failed to load data", "error");
      } finally {
        setLoading(false);
        setLoadingMemberships(false);
      }
    };

    fetchData();
  }, []);

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast("Payment link copied to clipboard", "success");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      showToast("Failed to copy link", "error");
    }
  };

  const handleDeleteClick = (link: PaymentLink) => {
    setLinkToDelete(link);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!linkToDelete) return;

    try {
      setDeletingLinkId(linkToDelete.paymentLinkId);
      await deletePaymentLink(linkToDelete.paymentLinkId);

      // Remove the deleted link from the state
      setPaymentLinks((prev) =>
        prev.filter(
          (link) => link.paymentLinkId !== linkToDelete.paymentLinkId,
        ),
      );
      showToast("Payment link deleted successfully", "success");
      setShowDeleteModal(false);
      setLinkToDelete(null);
    } catch (error) {
      console.error("Failed to delete payment link:", error);
      showToast("Failed to delete payment link", "error");
    } finally {
      setDeletingLinkId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setLinkToDelete(null);
  };

  const handleCreatePaymentLink = async () => {
    if (
      !createFormData.title.trim() ||
      !priceInput.trim() ||
      createFormData.price <= 0
    ) {
      showToast("Please enter a valid title and price", "error");
      return;
    }

    try {
      setSubmitting(true);
      await createPaymentLink(createFormData);
      showToast("Payment link created successfully", "success");
      setShowCreateModal(false);
      setCreateFormData({ title: "", interval: "month", price: 0 });
      setPriceInput("");

      // Refresh the payment links
      const paymentLinksResponse = await getExternalOnboardingPaymentLinks();
      setPaymentLinks(paymentLinksResponse.results);
    } catch (error) {
      console.error("Failed to create payment link:", error);
      showToast("Failed to create payment link", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="p-4">
        <div className="max-w-4xl">
          <div className="mb-3">
            <Breadcrumb className="mb-3">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem>Membership</BreadcrumbItem>
              <BreadcrumbItem href="/membership/manual-onboarding">
                Manual Onboarding
              </BreadcrumbItem>
            </Breadcrumb>
          </div>
          <div className="mb-4 relative">
            <div className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl mb-4">
              Manual Onboarding
            </div>
            {!loading && (
              <div className="absolute top-0 left-0 w-full max-w-4xl flex justify-end">
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2"
                >
                  <HiPlus className="h-4 w-4" />
                  Create Payment Link
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center p-6">
              <Spinner theme={blueThemeSpinner} color="info" size="lg" />
              <span className="ml-3 text-gray-500 dark:text-gray-400">
                Loading payment links...
              </span>
            </div>
          ) : (
            <Card className="p-4">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    External Payment Links
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Use these payment links for manual onboarding and external
                    membership creation.
                  </p>
                </div>

                {paymentLinks.length === 0 ? (
                  <div className="text-center p-6">
                    <p className="text-gray-500 dark:text-gray-400">
                      No payment links found.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentLinks.map((link) => (
                      <div
                        key={link.paymentLinkId}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 "
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                              {link.title}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              ${(link.price / 100).toFixed(2)}{" "}
                              {link.currency.toUpperCase()} /{" "}
                              {link.recurringInterval}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 items-center">
                          <TextInput
                            value={link.url}
                            readOnly
                            className="flex-1"
                            sizing="sm"
                          />
                          <Button
                            size="sm"
                            onClick={() => copyToClipboard(link.url)}
                            className="h-8"
                          >
                            <div className="flex items-center gap-1">
                              <HiClipboard className="w-4 h-4" />
                              Copy
                            </div>
                          </Button>
                          <DeleteButton
                            onDelete={() => handleDeleteClick(link)}
                            isDeleting={deletingLinkId === link.paymentLinkId}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card className="p-4 mt-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  External Memberships
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  View all external memberships and their redemption status.
                </p>
              </div>

              {loadingMemberships ? (
                <div className="flex justify-center items-center p-6">
                  <Spinner theme={blueThemeSpinner} color="info" size="lg" />
                  <span className="ml-3 text-gray-500 dark:text-gray-400">
                    Loading memberships...
                  </span>
                </div>
              ) : (
                <>
                  {externalMemberships.length === 0 ? (
                    <div className="text-center p-6">
                      <p className="text-gray-500 dark:text-gray-400">
                        No external memberships found.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeadCell>Name</TableHeadCell>
                            <TableHeadCell>Email</TableHeadCell>
                            <TableHeadCell>Redeem Status</TableHeadCell>
                          </TableRow>
                        </TableHead>
                        <TableBody className="divide-y">
                          {externalMemberships.map((membership) => (
                            <TableRow
                              key={membership.id}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                {membership.name}
                              </TableCell>
                              <TableCell className="text-gray-600 dark:text-gray-300">
                                {membership.email}
                              </TableCell>
                              <TableCell>
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    membership.redeem_status
                                      ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                                      : "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
                                  }`}
                                >
                                  {membership.redeem_status
                                    ? "Redeemed"
                                    : "Not Redeemed"}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Create Payment Link Modal */}
      <Modal
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateFormData({ title: "", interval: "month", price: 0 });
          setPriceInput("");
        }}
      >
        <ModalHeader>Create New Payment Link</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title" className="mb-2 block">
                Title
              </Label>
              <TextInput
                id="title"
                placeholder="e.g., Premium Monthly Plan"
                value={createFormData.title}
                onChange={(e) =>
                  setCreateFormData({
                    ...createFormData,
                    title: e.target.value,
                  })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="interval" className="mb-2 block">
                Billing Interval
              </Label>
              <Select
                id="interval"
                value={createFormData.interval}
                onChange={(e) =>
                  setCreateFormData({
                    ...createFormData,
                    interval: e.target.value as "month" | "year",
                  })
                }
                required
              >
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="price" className="mb-2 block">
                Price (USD)
              </Label>
              <TextInput
                id="price"
                type="number"
                placeholder="0.00"
                min="0"
                step="0.01"
                value={priceInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setPriceInput(value);
                  setCreateFormData({
                    ...createFormData,
                    price: value === "" ? 0 : parseFloat(value) || 0,
                  });
                }}
                required
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleCreatePaymentLink} disabled={submitting}>
            {submitting ? "Creating..." : "Create Payment Link"}
          </Button>
          <Button
            color="light"
            onClick={() => {
              setShowCreateModal(false);
              setCreateFormData({ title: "", interval: "month", price: 0 });
              setPriceInput("");
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Payment Link"
        message={`Are you sure you want to delete "${linkToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deletingLinkId === linkToDelete?.paymentLinkId}
      />

      {toastType && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast>
            <div
              className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                toastType === "success"
                  ? "bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200"
                  : "bg-red-100 text-red-500 dark:bg-red-800 dark:text-red-200"
              }`}
            >
              {toastType === "success" ? (
                <HiCheck className="h-5 w-5" />
              ) : (
                <HiX className="h-5 w-5" />
              )}
            </div>
            <div className="ml-3 text-sm font-normal">{toastMessage}</div>
            <ToastToggle onDismiss={() => setToastType(null)} />
          </Toast>
        </div>
      )}
    </NavbarSidebarLayout>
  );
};

export default ManualOnboardingPage;
